import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const cole2020 = {
  citation:
    "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
  url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
};
const cole2022 = {
  citation:
    "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
  url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Cuál es la diferencia entre SAINT® y Spravato?",
    answer:
      "SAINT® es estimulación magnética transcraneal acelerada administrada durante cinco días con orientación por fMRI. Spravato (esketamina) es un spray nasal autorizado por la FDA para la depresión resistente al tratamiento, administrado en una clínica certificada con monitorización. Actúan por mecanismos distintos.",
  },
  {
    question: "¿Puedo hacer SAINT® y Spravato?",
    answer:
      "En algunos casos se administran en combinación o en secuencia, según el plan del psiquiatra. La decisión depende del diagnóstico, el historial de tratamiento y la elegibilidad. Una consulta es el mejor primer paso.",
  },
];

export const esSaintVsSpravato = makeEsArticle({
  slug: "saint-vs-spravato",
  title: "SAINT® vs Spravato para la depresión",
  description:
    "SAINT® TMS frente a Spravato (esketamina) comparados: mecanismo, calendario, entorno de administración, estado de aprobación y quién puede ser evaluado. La idoneidad depende de las circunstancias.",
  h1: "SAINT® vs Spravato: ¿cuál es la diferencia?",
  breadcrumbLabel: "SAINT® vs Spravato",
  shortAnswer:
    "SAINT® y Spravato son tratamientos distintos para la depresión. SAINT® es estimulación magnética transcraneal acelerada administrada durante cinco días con orientación por fMRI. Spravato (esketamina) es un spray nasal autorizado por la FDA para la depresión resistente al tratamiento, administrado en una clínica certificada con monitorización posterior. Ambos requieren evaluación psiquiátrica. La idoneidad depende de las circunstancias individuales.",
  keyTakeaways: [
    "SAINT® es TMS acelerada con orientación por fMRI durante cinco días.",
    "Spravato (esketamina nasal) está autorizado por la FDA para la depresión resistente al tratamiento.",
    "Spravato se administra en una clínica certificada con monitorización.",
    "Ambos requieren evaluación psiquiátrica para confirmar la elegibilidad.",
    "La idoneidad depende de las circunstancias individuales.",
  ],
  sections: [
    {
      heading: "Mecanismo y administración",
      paragraphs: [
        "SAINT® utiliza estimulación magnética transcraneal theta burst para modular la actividad del circuito cerebral, con orientación individualizada por fMRI. Se administra durante cinco días consecutivos con diez sesiones por día.",
        "Spravato (esketamina) es un spray nasal que actúa como antagonista del receptor NMDA. Está autorizado por la FDA para la depresión resistente al tratamiento. Se administra en una clínica certificada, con monitorización durante al menos dos horas después de cada dosis, en una serie de sesiones a lo largo de varias semanas.",
      ],
    },
    {
      heading: "Calendario",
      paragraphs: [
        "SAINT® se completa en cinco días. Spravato se administra en sesiones programadas a lo largo de varias semanas, con una fase de inducción y luego una fase de mantenimiento.",
      ],
    },
    {
      heading: "¿Cuál podría ser adecuado para usted?",
      paragraphs: [
        "No hay una respuesta universal. La idoneidad depende del diagnóstico, el historial de tratamiento y otros factores. En algunos casos se administran en combinación o en secuencia. Una evaluación psiquiátrica es el primer paso.",
        "[[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [cole2020, cole2022],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
